import { decodeImage, inspectImage } from "../editor/imageIntake";
import { tintArtwork } from "./aiColor";
import type { AiResult, AiSource, AiStyle } from "./aiTypes";
import { removeConnectedWhiteBackground } from "./source-preparation/background";
import { ProductionAiClientError, ProductionAiJobClient } from "./service/productionAiClient";
import type { StencilGenerationProfile, StencilPromptOptions } from "../../../../api/src/stencilPrompt";

const endpoint = `${import.meta.env.BASE_URL}api/ai-stencil`;
export type AiGenerationProfile = StencilGenerationProfile;

function pngBlobFromPixels(data: Uint8ClampedArray, width: number, height: number): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Your browser could not prepare the image.");
  const canvasPixels = new Uint8ClampedArray(data.length);
  canvasPixels.set(data);
  context.putImageData(new ImageData(canvasPixels, width, height), 0, 0);
  return new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error("Your browser could not prepare the image.")), "image/png"));
}

export async function importAiSource(file: File, signal: AbortSignal): Promise<AiSource> {
  if (file.size > 10 * 1024 * 1024) throw new Error("Choose a PNG or JPEG smaller than 10 MB.");
  const header = await inspectImage(file, signal);
  const pixels = await decodeImage(header, 4_000_000, signal);
  signal.throwIfAborted();
  const name = file.name.replace(/\.[^.]+$/, "").slice(0, 120) || "Untitled stencil";
  const normalized = removeConnectedWhiteBackground(pixels.data, pixels.width, pixels.height);
  const blob = normalized.removedPixels > 0 ? await pngBlobFromPixels(normalized.data, normalized.width, normalized.height) : null;
  signal.throwIfAborted();
  const original: AiSource = {
    id: crypto.randomUUID(), name,
    url: URL.createObjectURL(file), width: pixels.width, height: pixels.height, example: false,
    bytes: header.bytes, mediaType: header.mediaType,
  };
  if (!blob) return original;
  return {
    id: crypto.randomUUID(), name, url: URL.createObjectURL(blob), width: normalized.width, height: normalized.height, example: false,
    bytes: new Uint8Array(await blob.arrayBuffer()), mediaType: "image/png", original,
  };
}

export function loadArtwork(url: string, signal?: AbortSignal): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const cleanup = () => signal?.removeEventListener("abort", abort);
    const abort = () => { cleanup(); image.src = ""; reject(new DOMException("Cancelled", "AbortError")); };
    if (signal?.aborted) { abort(); return; }
    signal?.addEventListener("abort", abort, { once: true });
    image.onload = () => { cleanup(); resolve(image); };
    image.onerror = () => { cleanup(); reject(new Error("This image could not be opened. Try another PNG or JPEG.")); };
    image.src = url;
  });
}

export async function createTintedArtworkUrl(url: string, color: string, signal?: AbortSignal): Promise<string> {
  const image = await loadArtwork(url, signal);
  const canvas = tintArtwork(image, color);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(value => value ? resolve(value) : reject(new Error("Your browser could not prepare the image.")), "image/png");
  });
  signal?.throwIfAborted();
  return URL.createObjectURL(blob);
}

async function normalizedInput(source: AiSource, signal: AbortSignal): Promise<string> {
  const image = await loadArtwork(source.url, signal);
  const scale = Math.min(1, 2048 / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Your browser could not prepare the image.");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  signal.throwIfAborted();
  return canvas.toDataURL("image/png");
}

function dataUrlBlob(value: string): Blob {
  const match = /^data:(image\/(?:png|jpeg));base64,(.+)$/.exec(value);
  if (!match?.[1] || !match[2]) throw new Error("Your browser could not prepare the image.");
  const bytes = Uint8Array.from(atob(match[2]), character => character.charCodeAt(0));
  return new Blob([bytes], { type: match[1] });
}

function blobDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The image service returned an unreadable response. Please try again."));
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("The image service returned an unreadable response. Please try again."));
    reader.readAsDataURL(blob);
  });
}

async function generateProductionStencil(source: AiSource, style: AiStyle, instructions: string, signal: AbortSignal, profile: AiGenerationProfile, promptOptions: StencilPromptOptions): Promise<AiResult> {
  const image = await normalizedInput(source, signal);
  const client = new ProductionAiJobClient();
  const asset = await client.uploadAsset(dataUrlBlob(image), signal);
  const requestId = crypto.randomUUID();
  const request = {
    requestId,
    cancellationId: crypto.randomUUID(),
    signal,
    documentId: `trial-${source.id}`,
    revision: 1,
    sourceAssetId: source.id,
    sourceHash: asset.contentHash,
    parentVariantId: `source-${source.id}`,
    style,
    profile,
    promptOptions,
    privateInstructions: instructions,
    idempotencyKey: requestId,
  } as const;
  let job = await client.createJob(request, asset.id);
  try {
    job = await client.waitForTerminal(job, signal);
  } catch (error) {
    if (signal.aborted) {
      const cleanup = new AbortController();
      await client.cancel(job.id, cleanup.signal).catch(() => undefined);
    }
    throw error;
  }
  if (job.status !== "succeeded") throw new ProductionAiClientError(job.errorCode ?? job.status, 502, job.errorCode === "provider_content_blocked"
    ? "We couldn't generate your image because copyrighted characters and celebrity likenesses aren't supported.\n\nDon't worry, no credits were deducted for this attempt."
    : "Generation failed. Your reference is safe. Please try again.");
  if (job.documentId !== request.documentId || job.documentRevision !== request.revision || job.parentVariantId !== request.parentVariantId || job.sourceHash !== request.sourceHash) {
    throw new ProductionAiClientError("stale_job", 409, "The stencil changed while it was being generated. Please try again.");
  }
  const result = await client.getResult(job.id, signal);
  const url = await blobDataUrl(result.blob);
  const resultImage = await loadArtwork(url, signal);
  return { id: job.id, source, url, width: resultImage.naturalWidth, height: resultImage.naturalHeight, style, instructions, createdAt: Date.parse(job.updatedAt), example: false };
}

export async function generateAiStencil(source: AiSource, style: AiStyle, instructions: string, signal: AbortSignal, profile: AiGenerationProfile = "default", promptOptions: StencilPromptOptions = {}): Promise<AiResult> {
  if (import.meta.env.PROD) {
    try { return await generateProductionStencil(source, style, instructions, signal, profile, promptOptions); }
    catch (error) {
      const unavailable = error instanceof ProductionAiClientError && (error.status === 404 || (error.status === 200 && error.code === "unreadable_response"));
      if (!unavailable) throw error;
      // Static previews and the local gateway do not expose the durable API.
      // Authentication, quota, validation, and provider failures never fall back.
    }
  }
  const image = await normalizedInput(source, signal);
  const response = await fetch(endpoint, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image, style, instructions, profile, promptOptions }), signal,
  });
  if (!response.headers.get("content-type")?.includes("application/json")) throw new Error("AI generation is not connected yet. Your reference is ready; try the example styles in the meantime.");
  const data: unknown = await response.json();
  if (!data || typeof data !== "object") throw new Error("The image service returned an unreadable response. Please try again.");
  const record = data as Record<string, unknown>;
  if (!response.ok) throw new Error(typeof record.error === "string" ? record.error : "Generation failed. Your reference is safe. Please try again.");
  if (typeof record.image !== "string" || !/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/]+=*$/.test(record.image) || record.image.length > 24_000_000) throw new Error("The image service did not return a valid stencil. Please try again.");
  const resultImage = await loadArtwork(record.image, signal);
  if (resultImage.naturalWidth * resultImage.naturalHeight > 24_000_000) throw new Error("The result exceeds the supported image size. Please try again.");
  return { id: crypto.randomUUID(), source, url: record.image, width: resultImage.naturalWidth, height: resultImage.naturalHeight, style, instructions, createdAt: Date.now(), example: false };
}
